import React, { useState, useEffect, useRef } from 'react';
import { WS_URL, WS_CLOSE_REVOKED, WS_CLOSE_TOKEN_EXPIRED } from '../config/env';
import { getAccessToken, notifySessionLost, onSessionRestored } from '../auth/session';
import { messageBoardService } from '../services/messageBoardService';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import BusMascot from '../components/BusMascot';
import './MessageBoard.css';
import { 
  BuildingOfficeIcon, 
  TruckIcon, 
  UsersIcon,
  ChevronDownIcon 
} from '@heroicons/react/24/outline';

// Helper to parse UTC timestamp
const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return new Date();
  const ts = String(timestamp);
  if (!ts.includes('Z') && !ts.includes('+') && !ts.includes('-', 10)) {
    return new Date(ts + 'Z');
  }
  return new Date(ts);
};

// Helper to parse date string without timezone conversion (for event dates)
const parseDateLocal = (dateStr) => {
  if (!dateStr) return new Date();
  const dateOnly = String(dateStr).split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// WebSocket URL comes from config/env.js (P1-B)
const getWsUrl = () => WS_URL;

// Audience Dropdown Component
const AudienceDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: 'internal', label: 'Internal Staff Only', description: 'Page will only show to office staff', icon: BuildingOfficeIcon },
    { value: 'drivers', label: 'Drivers Only', description: 'For GVC Driver App only', icon: TruckIcon },
    { value: 'all', label: 'Everyone', description: 'Page will show to all users', icon: UsersIcon }
  ];

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="mb-dropdown-btn">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <selectedOption.icon style={{ width: 20, height: 20, color: '#A0A0A0' }} />
          <span>{selectedOption.label}</span>
        </div>
        <ChevronDownIcon style={{ width: 16, height: 16, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {isOpen && (
        <div className="mb-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => { onChange(option.value); setIsOpen(false); }}
              className={`mb-dropdown-item ${value === option.value ? 'active' : ''}`}
            >
              <option.icon style={{ width: 22, height: 22, color: value === option.value ? '#D4A94E' : '#A0A0A0' }} />
              <div>
                <div style={{ fontWeight: 500 }}>{option.label}</div>
                <div style={{ fontSize: 12, color: '#A0A0A0' }}>{option.description}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Audio Broadcast Component with WebSocket streaming
const AudioBroadcast = ({ wsRef, isConnected }) => {
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [listenerCount, setListenerCount] = useState(0);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const processorRef = useRef(null);
  const animationFrameRef = useRef(null);

  const startBroadcast = async () => {
    try {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        toast.error('Not connected to server. Please wait...');
        return;
      }
      
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true, 
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1
        } 
      });
      mediaStreamRef.current = stream;
      
      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioContextRef.current = audioContext;
      
      const source = audioContext.createMediaStreamSource(stream);
      
      // Create analyser for level meter
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      source.connect(analyser);
      
      // Create script processor for sending audio data
      const bufferSize = 4096;
      const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
      processorRef.current = processor;
      
      processor.onaudioprocess = (e) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);
          // Convert float32 to int16 for smaller payload
          const int16Data = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            int16Data[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
          }
          wsRef.current.send(int16Data.buffer);
        }
      };
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Register as broadcaster
      wsRef.current.send(JSON.stringify({ type: 'register', role: 'broadcaster' }));
      
      // Start level meter animation
      const updateLevel = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average / 255);
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
      
      setIsBroadcasting(true);
      toast.success('🎤 Broadcasting started!');
      
    } catch (err) {
      console.error('Broadcast error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access.');
      } else {
        toast.error('Could not start broadcast: ' + err.message);
      }
      stopBroadcast();
    }
  };

  const stopBroadcast = () => {
    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Stop audio processing
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Notify server
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop_broadcast' }));
    }
    
    setIsBroadcasting(false);
    setAudioLevel(0);
    setListenerCount(0);
    
    toast.info('📻 Broadcast ended');
  };

  // Listen for status updates
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        if (typeof event.data === 'string') {
          const message = JSON.parse(event.data);
          if (message.type === 'status') {
            setListenerCount(message.listenerCount || 0);
          }
        }
      } catch (e) {
        // Ignore
      }
    };
    
    if (wsRef.current) {
      wsRef.current.addEventListener('message', handleMessage);
      return () => {
        if (wsRef.current) {
          wsRef.current.removeEventListener('message', handleMessage);
        }
      };
    }
  }, [wsRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (processorRef.current) processorRef.current.disconnect();
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(track => track.stop());
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close();
    };
  }, []);

  return (
    <div className={`audio-broadcast ${isBroadcasting ? 'active' : ''}`}>
      {isBroadcasting ? (
        <>
          <div className="broadcast-indicator">
            <span className="broadcast-dot"></span>
            <span>BROADCASTING</span>
          </div>
          <div className="audio-level-bar">
            <div className="audio-level-fill" style={{ width: `${audioLevel * 100}%` }}></div>
          </div>
          {listenerCount > 0 && (
            <div style={{ fontSize: 12, color: '#4ADE80', marginBottom: 8, textAlign: 'center' }}>
              📺 {listenerCount} display{listenerCount !== 1 ? 's' : ''} connected
            </div>
          )}
          <button className="broadcast-btn stop" onClick={stopBroadcast}>
            ⏹️ Stop Broadcasting
          </button>
        </>
      ) : (
        <button className="broadcast-btn start" onClick={startBroadcast} disabled={!isConnected}>
          🎤 Start Broadcasting
        </button>
      )}
      <p className="broadcast-hint">
        {isBroadcasting 
          ? 'Your voice is being broadcast to the facility speakers' 
          : isConnected 
            ? 'Click to broadcast your voice through facility speakers'
            : 'Connecting to server...'}
      </p>
    </div>
  );
};

const MessageBoard = () => {
  const { isAdmin, isManager } = useAuth();
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('messages');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showPageModal, setShowPageModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // WebSocket state for Voice paging
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  
  const [newMessage, setNewMessage] = useState({ title: '', content: '', category: 'general', priority: 'normal' });
  const [newEvent, setNewEvent] = useState({ title: '', description: '', event_date: '', event_time: '', location: '', show_in_app: true, show_on_website: false });
  const [newPage, setNewPage] = useState({ employee_name: '', message: '', priority: 'normal', audience: 'internal' });

  const canManagePages = isAdmin || isManager;

  // Connect to WebSocket for Voice paging.
  // P1-B: a fresh access token on every (re)connect, exponential backoff
  // 1s→30s, server close codes honoured (4408 token expired → quick reconnect
  // with a fresh token; 4401 session revoked → stop, sign-in modal).
  useEffect(() => {
    let stopped = false;
    let attempt = 0;
    let timer = null;

    const schedule = (quick = false) => {
      if (stopped) return;
      const delay = quick ? 1000 : Math.min(30000, 1000 * 2 ** attempt);
      attempt += 1;
      clearTimeout(timer);
      timer = setTimeout(() => connectWebSocket(), delay);
    };

    const connectWebSocket = async () => {
      if (stopped) return;
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) return;
      console.log('🔌 Connecting to WebSocket...');
      const token = await getAccessToken();
      if (!token) { schedule(); return; }
      const ws = new WebSocket(getWsUrl(), ['gvc-portal', token]);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        attempt = 0;
        setWsConnected(true);
      };
      
      // This page only sends (voice paging); the server's status messages
      // carry nothing it needs to act on.
      ws.onmessage = () => {};
      
      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        setWsConnected(false);
      };
      
      ws.onclose = (event) => {
        console.log('📴 WebSocket disconnected', event.code);
        setWsConnected(false);
        if (event.code === WS_CLOSE_REVOKED) {
          stopped = true;
          notifySessionLost({ code: 'SESSION_REVOKED' });
          return;
        }
        schedule(event.code === WS_CLOSE_TOKEN_EXPIRED);
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    const offRestored = onSessionRestored(() => { stopped = false; attempt = 0; connectWebSocket(); });
    
    return () => {
      stopped = true;
      clearTimeout(timer);
      offRestored();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [messagesData, eventsData, pagesData] = await Promise.all([
        messageBoardService.getMessages(), messageBoardService.getEvents(), messageBoardService.getPages()
      ]);
      setMessages(messagesData.messages || []);
      setEvents(eventsData.events || []);
      setPages(pagesData.pages || []);
    } catch (err) { toast.error('Failed to load message board'); }
    finally { setLoading(false); }
  };

  const openDisplayPage = () => { window.open('/display', '_blank'); };

  const handleCreateMessage = async (e) => {
    e.preventDefault();
    try { await messageBoardService.createMessage(newMessage); toast.success('Message created!'); setShowMessageModal(false); setNewMessage({ title: '', content: '', category: 'general', priority: 'normal' }); fetchData(); }
    catch (err) { toast.error('Failed to create message'); }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try { await messageBoardService.createEvent(newEvent); toast.success('Event created!'); setShowEventModal(false); setNewEvent({ title: '', description: '', event_date: '', event_time: '', location: '', show_in_app: true, show_on_website: false }); fetchData(); }
    catch (err) { toast.error('Failed to create event'); }
  };

  const handleCreatePage = async (e) => {
    e.preventDefault();
    try { await messageBoardService.createPage(newPage); toast.success('Page request sent!'); setShowPageModal(false); setNewPage({ employee_name: '', message: '', priority: 'normal', audience: 'internal' }); fetchData(); }
    catch (err) { toast.error('Failed to send page'); }
  };

  const handleCancelPage = async (pageId) => { try { await messageBoardService.cancelPage(pageId); toast.success('Page cancelled'); setShowCancelConfirm(null); fetchData(); } catch (err) { toast.error('Failed to cancel page'); } };
  const handleDeletePage = async (pageId) => { try { await messageBoardService.deletePage(pageId); toast.success('Page deleted'); setShowDeleteConfirm(null); fetchData(); } catch (err) { toast.error('Failed to delete page'); } };
  const handleAcknowledgePage = async (pageId) => { try { await messageBoardService.acknowledgePage(pageId); toast.success('Page acknowledged'); fetchData(); } catch (err) { toast.error('Failed to acknowledge page'); } };
  const handleDeleteMessage = async (messageId) => { if (!window.confirm('Delete this message?')) return; try { await messageBoardService.deleteMessage(messageId); toast.success('Message deleted'); fetchData(); } catch (err) { toast.error('Failed to delete message'); } };
  const handleDeleteEvent = async (eventId) => { if (!window.confirm('Delete this event?')) return; try { await messageBoardService.deleteEvent(eventId); toast.success('Event deleted'); fetchData(); } catch (err) { toast.error('Failed to delete event'); } };

  const getAudienceBadge = (audience) => {
    switch (audience) {
      case 'drivers': return { label: 'Drivers', color: '#60A5FA', bg: 'rgba(96,165,250,0.15)' };
      case 'all': return { label: 'Everyone', color: '#4ADE80', bg: 'rgba(74,222,128,0.15)' };
      default: return { label: 'Internal', color: '#A0A0A0', bg: 'rgba(160,160,160,0.15)' };
    }
  };

  const activeMessages = messages.filter(m => m.is_active);
  const activePages = pages.filter(p => p.is_active && !p.is_acknowledged);
  const upcomingEvents = events.filter(e => parseDateLocal(e.event_date) >= new Date(new Date().setHours(0,0,0,0)));
  const recentActivity = [...messages.slice(0, 3).map(m => ({ type: 'message', data: m, time: m.created_at })), ...pages.slice(0, 3).map(p => ({ type: 'page', data: p, time: p.created_at }))].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 5);

  if (loading) { return <div className="mb-loading">Loading message board...</div>; }

  return (
    <div className="message-board">
      <div className="mb-page-header">
        <div><h1>📋 Message Board</h1><p>Manage announcements, events, and employee paging</p></div>
        <div className="mb-header-actions">
          <button className="mb-btn mb-btn-secondary" onClick={openDisplayPage}>📺 Open Pi Display</button>
          <button className="mb-btn mb-btn-danger" onClick={() => setShowPageModal(true)}>🚨 Emergency Alert</button>
          <button className="mb-btn mb-btn-primary" onClick={() => setShowMessageModal(true)}>+ New Announcement</button>
        </div>
      </div>

      <div className="mb-content-grid">
        <div className="mb-left-column">
          <div className="mb-stats-row">
            <div className="mb-stat-card" onClick={() => setActiveTab('messages')} style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
              <div className="mb-stat-icon orange">🔔</div><div className="mb-stat-value">{activeMessages.length}</div><div className="mb-stat-label">Active Messages</div>
              <span className="mb-stat-action" onClick={(e) => { e.stopPropagation(); setShowMessageModal(true); }}>+ Add New</span>
            </div>
            <div className="mb-stat-card" onClick={() => setActiveTab('events')} style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
              <div className="mb-stat-icon green">📅</div><div className="mb-stat-value">{upcomingEvents.length}</div><div className="mb-stat-label">Upcoming Events</div>
              <span className="mb-stat-action" onClick={(e) => { e.stopPropagation(); setShowEventModal(true); }}>+ Add New</span>
            </div>
            <div className="mb-stat-card" onClick={() => setActiveTab('pages')} style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
              <div className="mb-stat-icon blue">📟</div><div className="mb-stat-value">{activePages.length}</div><div className="mb-stat-label">Active Pages</div>
              <span className="mb-stat-action" onClick={(e) => { e.stopPropagation(); setShowPageModal(true); }}>+ Page</span>
            </div>
          </div>

          <div className="mb-card">
            <div className="mb-tabs">
              <button className={`mb-tab ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')}>🔔 Messages</button>
              <button className={`mb-tab ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')}>📅 Events</button>
              <button className={`mb-tab ${activeTab === 'pages' ? 'active' : ''}`} onClick={() => setActiveTab('pages')}>📟 Pages {activePages.length > 0 && <span className="mb-tab-badge">{activePages.length}</span>}</button>
            </div>

            <div className="mb-card-body">
              {activeTab === 'messages' && (
                <div className="mb-message-list">
                  {messages.length === 0 ? (<div className="mb-empty-state"><div className="icon">🔔</div><p>No active messages. Create your first announcement!</p></div>) : (
                    messages.map(msg => (
                      <div key={msg.id} className="mb-message-item">
                        <div className={`mb-message-priority ${msg.priority}`}></div>
                        <div className="mb-message-content">
                          <div className="mb-message-title">{msg.title}<span className={`mb-badge ${msg.priority}`}>{msg.priority}</span><span className="mb-badge" style={{ background: 'rgba(160,160,160,0.15)', color: '#E0E0E0' }}>{msg.category}</span></div>
                          <div className="mb-message-text">{msg.content}</div>
                          <div className="mb-message-meta">{msg.created_by_name || 'Admin'} • {parseUTCTimestamp(msg.created_at).toLocaleString()}</div>
                        </div>
                        <div className="mb-message-actions"><button className="mb-action-btn delete" onClick={() => handleDeleteMessage(msg.id)}>🗑️</button></div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'events' && (
                <div className="mb-message-list">
                  {events.length === 0 ? (<div className="mb-empty-state"><div className="icon">📅</div><p>No upcoming events. Add your first event!</p></div>) : (
                    events.map(event => {
                      const eventDate = parseDateLocal(event.event_date);
                      return (
                        <div key={event.id} className="mb-event-item">
                          <div className="mb-event-date"><div className="mb-event-month">{eventDate.toLocaleDateString('en-US', { month: 'short' })}</div><div className="mb-event-day">{eventDate.getDate()}</div></div>
                          <div className="mb-event-details">
                            <div className="mb-event-title">{event.title}{event.show_in_app && <span className="mb-badge" style={{ background: 'rgba(96,165,250,0.15)', color: '#60A5FA', marginLeft: 8 }}>📱 App</span>}{event.show_on_website && <span className="mb-badge" style={{ background: 'rgba(74,222,128,0.15)', color: '#4ADE80', marginLeft: 4 }}>🌐 Web</span>}</div>
                            <div className="mb-event-time">{event.event_time && `🕐 ${event.event_time}`}{event.location && ` • 📍 ${event.location}`}{event.description && ` • ${event.description}`}</div>
                          </div>
                          <div className="mb-message-actions"><button className="mb-action-btn delete" onClick={() => handleDeleteEvent(event.id)}>🗑️</button></div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {activeTab === 'pages' && (
                <div className="mb-message-list">
                  {pages.length === 0 ? (<div className="mb-empty-state"><div className="icon">📟</div><p>No active pages. Send a page to an employee!</p></div>) : (
                    pages.map(page => {
                      const audienceBadge = getAudienceBadge(page.audience);
                      return (
                        <div key={page.id} className="mb-page-item">
                          <div className="mb-page-header"><span className="mb-page-icon">📟</span><span className="mb-page-name">{page.employee_name}</span><span className={`mb-badge ${page.priority}`}>{page.priority}</span><span className="mb-badge" style={{ background: audienceBadge.bg, color: audienceBadge.color }}>{audienceBadge.label}</span>{page.is_active && !page.is_acknowledged && (<span className="mb-badge live">🔴 LIVE</span>)}</div>
                          <div className="mb-page-message">{page.message}</div>
                          <div className="mb-page-footer">
                            <span className="mb-page-time">{parseUTCTimestamp(page.created_at).toLocaleString()}</span>
                            <div className="mb-page-actions">
                              {page.is_acknowledged ? (<><span className="mb-ack-badge">✓ Ack by {page.acknowledged_by_name}</span>{canManagePages && (<button className="mb-btn-small mb-btn-delete" onClick={() => setShowDeleteConfirm(page)}>Delete</button>)}</>) : page.is_active ? (canManagePages && (<><button className="mb-btn-small mb-btn-ack" onClick={() => handleAcknowledgePage(page.id)}>✓ Ack</button><button className="mb-btn-small mb-btn-cancel" onClick={() => setShowCancelConfirm(page)}>Stop</button></>)) : (canManagePages && (<button className="mb-btn-small mb-btn-delete" onClick={() => setShowDeleteConfirm(page)}>Delete</button>))}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mb-right-column">
          <div className="mb-card"><div className="mb-card-header"><h3>📻 Voice Paging</h3></div><AudioBroadcast wsRef={wsRef} isConnected={wsConnected} /></div>

          <div className="mb-card">
            <div className="mb-card-header"><h3>⚡ Quick Actions</h3></div>
            <div className="mb-quick-actions">
              <div className="mb-quick-action" onClick={() => setShowMessageModal(true)}><div className="icon">📢</div><div className="label">Announce</div></div>
              <div className="mb-quick-action" onClick={() => { setNewPage({...newPage, priority: 'urgent'}); setShowPageModal(true); }}><div className="icon">🚨</div><div className="label">Emergency</div></div>
              <div className="mb-quick-action" onClick={() => setShowPageModal(true)}><div className="icon">📟</div><div className="label">Page</div></div>
              <div className="mb-quick-action" onClick={() => setShowEventModal(true)}><div className="icon">📅</div><div className="label">Event</div></div>
            </div>
          </div>

          <div className="mb-card">
            <div className="mb-card-header"><h3>📊 Recent Activity</h3></div>
            <div className="mb-activity-list">
              {recentActivity.length === 0 ? (<div className="mb-empty-state" style={{ padding: '20px' }}><p>No recent activity</p></div>) : (
                recentActivity.map((item, idx) => (
                  <div key={idx} className="mb-activity-item">
                    <div className={`mb-activity-icon ${item.type === 'message' ? 'sent' : 'alert'}`}>{item.type === 'message' ? '📢' : '📟'}</div>
                    <div className="mb-activity-text"><strong>{item.type === 'message' ? item.data.title : item.data.employee_name}</strong>{item.type === 'page' && ' paged'}</div>
                    <div className="mb-activity-time">{new Date(item.time).toLocaleDateString()}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {showMessageModal && (
        <div className="mb-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="mb-modal" onClick={e => e.stopPropagation()}>
            <div className="mb-modal-header"><h2>📢 Add New Message</h2><button className="mb-modal-close" onClick={() => setShowMessageModal(false)}>×</button></div>
            <form onSubmit={handleCreateMessage}>
              <div className="mb-modal-body">
                <div className="mb-form-group"><label>Title *</label><input type="text" value={newMessage.title} onChange={e => setNewMessage({...newMessage, title: e.target.value})} required /></div>
                <div className="mb-form-group"><label>Content *</label><textarea value={newMessage.content} onChange={e => setNewMessage({...newMessage, content: e.target.value})} required rows={4} /></div>
                <div className="mb-form-row">
                  <div className="mb-form-group"><label>Category</label><select value={newMessage.category} onChange={e => setNewMessage({...newMessage, category: e.target.value})}><option value="general">General</option><option value="announcement">Announcement</option><option value="safety">Safety</option><option value="hr">HR</option><option value="operations">Operations</option></select></div>
                  <div className="mb-form-group"><label>Priority</label><select value={newMessage.priority} onChange={e => setNewMessage({...newMessage, priority: e.target.value})}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>
                </div>
              </div>
              <div className="mb-modal-footer"><button type="button" className="mb-btn mb-btn-secondary" onClick={() => setShowMessageModal(false)}>Cancel</button><button type="submit" className="mb-btn mb-btn-primary">Create Message</button></div>
            </form>
          </div>
        </div>
      )}

      {showEventModal && (
        <div className="mb-modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="mb-modal" onClick={e => e.stopPropagation()}>
            <div className="mb-modal-header"><h2>📅 Add New Event</h2><button className="mb-modal-close" onClick={() => setShowEventModal(false)}>×</button></div>
            <form onSubmit={handleCreateEvent}>
              <div className="mb-modal-body">
                <div className="mb-form-group"><label>Title *</label><input type="text" value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} required /></div>
                <div className="mb-form-group"><label>Description</label><textarea value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} rows={3} /></div>
                <div className="mb-form-row">
                  <div className="mb-form-group"><label>Date *</label><input type="date" value={newEvent.event_date} onChange={e => setNewEvent({...newEvent, event_date: e.target.value})} required /></div>
                  <div className="mb-form-group"><label>Time</label><input type="time" value={newEvent.event_time} onChange={e => setNewEvent({...newEvent, event_time: e.target.value})} /></div>
                </div>
                <div className="mb-form-group"><label>Location</label><input type="text" value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} placeholder="e.g., Main Office, Conference Room" /></div>
                <div className="mb-form-group">
                  <label>Publish To</label>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}><input type="checkbox" checked={newEvent.show_in_app} onChange={e => setNewEvent({...newEvent, show_in_app: e.target.checked})} style={{ width: '18px', height: '18px' }} /><span>📱 Mobile App</span></label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}><input type="checkbox" checked={newEvent.show_on_website} onChange={e => setNewEvent({...newEvent, show_on_website: e.target.checked})} style={{ width: '18px', height: '18px' }} /><span>🌐 Website</span></label>
                  </div>
                  <small style={{ color: '#A0A0A0', marginTop: '4px', display: 'block' }}>Events always show on Raspberry Pi display</small>
                </div>
              </div>
              <div className="mb-modal-footer"><button type="button" className="mb-btn mb-btn-secondary" onClick={() => setShowEventModal(false)}>Cancel</button><button type="submit" className="mb-btn mb-btn-primary">Create Event</button></div>
            </form>
          </div>
        </div>
      )}

      {showPageModal && (
        <div className="mb-modal-overlay" onClick={() => setShowPageModal(false)}>
          <div className="mb-modal" onClick={e => e.stopPropagation()}>
            <div className="mb-modal-header"><h2>📟 Send Page</h2><button className="mb-modal-close" onClick={() => setShowPageModal(false)}>×</button></div>
            <form onSubmit={handleCreatePage}>
              <div className="mb-modal-body">
                <div className="mb-form-group"><label>Employee Name *</label><input type="text" value={newPage.employee_name} onChange={e => setNewPage({...newPage, employee_name: e.target.value})} required /></div>
                <div className="mb-form-group"><label>Message *</label><textarea value={newPage.message} onChange={e => setNewPage({...newPage, message: e.target.value})} required rows={3} /></div>
                <div className="mb-form-row">
                  <div className="mb-form-group"><label>Audience</label><AudienceDropdown value={newPage.audience} onChange={(value) => setNewPage({...newPage, audience: value})} /></div>
                  <div className="mb-form-group"><label>Priority</label><select value={newPage.priority} onChange={e => setNewPage({...newPage, priority: e.target.value})}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>
                </div>
              </div>
              <div className="mb-modal-footer"><button type="button" className="mb-btn mb-btn-secondary" onClick={() => setShowPageModal(false)}>Cancel</button><button type="submit" className="mb-btn mb-btn-primary">Send Page</button></div>
            </form>
          </div>
        </div>
      )}

      {showCancelConfirm && (
        <div className="mb-modal-overlay" onClick={() => setShowCancelConfirm(null)}>
          <div className="mb-modal mb-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="mb-modal-body" style={{ textAlign: 'center', padding: '30px' }}><div className="mb-confirm-icon">⚠️</div><h2>Stop Page Alert?</h2><p>Stop page for <strong>{showCancelConfirm.employee_name}</strong>?</p></div>
            <div className="mb-modal-footer"><button className="mb-btn mb-btn-secondary" onClick={() => setShowCancelConfirm(null)}>Keep Active</button><button className="mb-btn mb-btn-danger" onClick={() => handleCancelPage(showCancelConfirm.id)}>Stop Page</button></div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="mb-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="mb-modal mb-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="mb-modal-body" style={{ textAlign: 'center', padding: '30px' }}><div className="mb-confirm-icon">🗑️</div><h2>Delete Page?</h2><p>Delete page for <strong>{showDeleteConfirm.employee_name}</strong>?</p></div>
            <div className="mb-modal-footer"><button className="mb-btn mb-btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button><button className="mb-btn mb-btn-danger" onClick={() => handleDeletePage(showDeleteConfirm.id)}>Delete</button></div>
          </div>
        </div>
      )}

      <BusMascot size={120} animated={true} showBubble={true} onClick={openDisplayPage} />
    </div>
  );
};

export default MessageBoard;
